<template>
  <v-row class="device-info-panel">
    <v-col cols="12">
      <v-card>
        <v-card-title class="justify-center">
          <div class="text-center">
            Device ID:
            <v-chip label small>
              {{ device.device_id }}
            </v-chip>
          </div>
        </v-card-title>

        <v-card-text>
          <h2 class="text-xl font-weight-semibold mb-2">Details</h2>
          <v-divider class="mt-2 mb-2"></v-divider>

          <v-list-item dense class="px-0">
            <v-row>
              <v-col class="d-flex" cols="5">
                <span class="font-weight-medium me-2">Vehicle ID:</span>
              </v-col>
              <v-col cols="7">
                <router-link
                  v-if="device.config.vehicle_id"
                  :to="{ name: 'next-stop-vehicle-config', params: { id: device.config.vehicle_id } }"
                  class="cursor-pointer text-decoration-none"
                >
                  <v-chip link small>{{ device.config.vehicle_id }}</v-chip>
                </router-link>
                <span v-else class="text--secondary">Unknown</span>
              </v-col>
            </v-row>
          </v-list-item>

          <v-list>
            <v-list-item dense class="px-0">
              <v-row>
                <v-col class="d-flex" cols="5">
                  <span class="font-weight-medium me-2">Status:</span>
                </v-col>
                <v-col cols="7">
                  <span class="text--secondary">
                    {{ getFormattedDeviceStatus(device) }}
                  </span>
                </v-col>
              </v-row>
            </v-list-item>

            <v-list-item dense class="px-0">
              <v-row>
                <v-col class="d-flex" cols="5">
                  <span class="font-weight-medium me-2">Reachable:</span>
                </v-col>
                <v-col cols="7">
                  <span class="text--secondary">
                    {{ reachableText }}
                  </span>
                </v-col>
              </v-row>
            </v-list-item>

            <v-list-item dense class="px-0">
              <v-row>
                <v-col class="d-flex" cols="5">
                  <span class="font-weight-medium me-2">Reachable Reason:</span>
                </v-col>
                <v-col cols="7">
                  <span class="text--secondary">
                    {{ reachableReason }}
                  </span>
                </v-col>
              </v-row>
            </v-list-item>

            <v-list-item dense class="px-0">
              <v-row>
                <v-col class="d-flex" cols="5">
                  <span class="font-weight-medium me-2">Services Not Running:</span>
                </v-col>
                <v-col cols="7">
                  <span class="text--secondary">
                    {{ servicesNotRunning.length ? servicesNotRunning.join(", ") : "-" }}
                  </span>
                </v-col>
              </v-row>
            </v-list-item>

            <v-list-item dense class="px-0">
              <v-row>
                <v-col class="d-flex" cols="5">
                  <span class="font-weight-medium me-2">SSD ID:</span>
                </v-col>
                <v-col cols="7">
                  <span class="text--secondary">
                    {{ device.config.ssd_serial ? device.config.ssd_serial : "Unknown" }}
                  </span>
                </v-col>
              </v-row>
            </v-list-item>

            <v-list-item dense class="px-0">
              <v-row>
                <v-col class="d-flex" cols="5">
                  <span class="font-weight-medium me-2">Hardware ID:</span>
                </v-col>
                <v-col cols="7">
                  <span class="text--secondary">
                    {{ device.config.case_id ? device.config.case_id : "Unknown" }}
                  </span>
                </v-col>
              </v-row>
            </v-list-item>

            <v-list-item dense class="px-0">
              <v-row>
                <v-col class="d-flex" cols="5">
                  <span class="font-weight-medium me-2">Tags:</span>
                </v-col>
                <v-col cols="7">
                  <TagChips v-if="deviceTags.length" :config="device.config" />
                  <span v-else class="text--secondary">-</span>
                </v-col>
              </v-row>
            </v-list-item>

            <v-list-item dense class="px-0">
              <v-row>
                <v-col class="d-flex" cols="5">
                  <span class="font-weight-medium me-2">Release Version:</span>
                </v-col>
                <v-col cols="7">
                  <span class="text--secondary">
                    {{ device.status.release_version ? device.status.release_version : "Unknown" }}
                  </span>
                </v-col>
              </v-row>
            </v-list-item>

            <v-list-item dense class="px-0">
              <v-row>
                <v-col class="d-flex" cols="5">
                  <span class="font-weight-medium me-2">Target Release Version:</span>
                </v-col>
                <v-col cols="7">
                  <span class="text--secondary">
                    {{ device.status.target_release_version ? device.status.target_release_version : "Unknown" }}
                  </span>
                </v-col>
              </v-row>
            </v-list-item>

            <v-list-item dense class="px-0">
              <v-row>
                <v-col class="d-flex" cols="5">
                  <span class="font-weight-medium me-2">Last Updated:</span>
                </v-col>
                <v-col cols="7">
                  <span class="text--secondary">
                    {{
                      device.status.device_timestamp ? formatDate(device.status.device_timestamp, "en-NZ") : "Unknown"
                    }}
                  </span>
                </v-col>
              </v-row>
            </v-list-item>

            <v-list-item dense class="px-0">
              <v-row>
                <v-col class="d-flex" cols="5">
                  <span class="font-weight-medium me-2">Location Organisation:</span>
                </v-col>
                <v-col cols="7">
                  <span class="text--secondary">
                    {{ device.location && device.location.organisation ? device.location.organisation : "-" }}
                  </span>
                </v-col>
              </v-row>
            </v-list-item>

            <v-list-item dense class="px-0">
              <v-row>
                <v-col class="d-flex" cols="5">
                  <span class="font-weight-medium me-2">Location Status:</span>
                </v-col>
                <v-col cols="7">
                  <span class="text--secondary text-pre-wrap">
                    {{ device.location && device.location.status ? device.location.status : "-" }}
                  </span>
                </v-col>
              </v-row>
            </v-list-item>
          </v-list>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { mapState } from "vuex"
import "mapbox-gl/dist/mapbox-gl.css"
import globalHelpers from "@/mixins/globalHelpers"
import TagChips from "@/views/next-stop/components/TagChips.vue"
import { getConfigTags } from "@/utils/tags"

export default {
  components: { TagChips },
  mixins: [globalHelpers],

  computed: {
    ...mapState({
      device: state => state.selected_device,
    }),
    deviceTags() {
      return getConfigTags(this.device?.config)
    },
    // Older payloads have no is_reachable at all, which is not the same as the
    // device being unreachable, so that case reads as unknown rather than No
    reachableText() {
      const reachable = this.device?.status?.is_reachable?.reachable

      if (typeof reachable !== "boolean") {
        return "Unknown"
      }

      return reachable ? "Yes" : "No"
    },
    reachableReason() {
      return this.device?.status?.is_reachable?.reason || "-"
    },
    servicesNotRunning() {
      return this.device?.status?.services_not_running || []
    },
  },

  methods: {
    getFormattedDeviceStatus(device) {
      return device.status.status ?? "Unknown"
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/styles/next-stop/components/map.scss";
</style>
